import { useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useExcluirArquivosFiscais,
  type FiltroArquivosFiscais,
} from '../../shared/api/queries.ts';
import type { Cliente } from '../../shared/api/tipos.ts';
import { formatarDataSimples } from '../../shared/format.ts';

/**
 * Confirmação da exclusão das notas do filtro.
 *
 * É a única ação do painel que destrói documento fiscal, e por isso ela é a
 * mais cara de disparar: o diálogo mostra o cliente, o período e **quantas**
 * notas vão embora, pede o motivo por escrito e só então libera o botão. O
 * resto do sistema parte do princípio de que nota recebida fica — quem chega
 * aqui está consertando o que entrou errado, e precisa ter certeza de que está
 * olhando a loja certa.
 *
 * O recorte é o filtro **aplicado**, o mesmo da lista atrás do diálogo: o que
 * se apaga é o que está na tela, nunca mais do que isso. É também por isso que
 * a contagem vem do resumo do servidor e não do tamanho da página.
 *
 * O motivo não vira campo de nenhuma tela: ele vai para o log do servidor, que
 * é a única resposta que sobra quando alguém perguntar, meses depois, onde
 * foram as notas daquele mês.
 */
export function DialogoExclusao({
  cliente,
  filtro,
  quantidade,
  aoFechar,
  aoExcluido,
}: {
  cliente: Cliente;
  filtro: FiltroArquivosFiscais & { empresaId: string };
  quantidade: number;
  aoFechar: () => void;
  aoExcluido: (excluidos: number) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const excluir = useExcluirArquivosFiscais();

  const motivoCurto = motivo.trim().length < 5;

  async function confirmar() {
    const resultado = await excluir.mutateAsync({ ...filtro, motivo: motivo.trim() });
    aoExcluido(resultado.excluidos);
    aoFechar();
  }

  return (
    <Dialog open onClose={aoFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Excluir arquivos fiscais</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="error" icon={<DeleteForeverOutlinedIcon />}>
            <AlertTitle>Não tem como desfazer</AlertTitle>
            O XML e o registro de cada nota são apagados do servidor. Se a loja
            ainda tiver os arquivos, o client pode mandá-los de novo; se não
            tiver, eles acabam aqui.
          </Alert>

          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Cliente
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>{cliente.razaoSocial}</Typography>
            <Typography variant="body2" color="text.secondary">
              {cliente.cnpjFormatado}
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              O que vai ser apagado
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {quantidade} nota(s)
              {filtro.de !== undefined && filtro.ate !== undefined
                ? ` emitidas de ${formatarDataSimples(filtro.de)} a ${formatarDataSimples(filtro.ate)}`
                : ' — todo o histórico deste cliente'}
            </Typography>
            {/* Dizer que o filtro vale também aqui evita a leitura de que o
                botão apaga sempre tudo: quem chegou com chave ou situação
                filtrada está apagando um recorte, e precisa ver isso. */}
            <Typography variant="body2" color="text.secondary">
              É o filtro aplicado nesta tela, e só ele.
            </Typography>
          </Stack>

          <TextField
            label="Motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            required
            multiline
            minRows={2}
            helperText="Fica no registro do servidor: é o que responde, meses depois, por que estas notas não estão mais aqui."
          />

          {excluir.isError && (
            <Alert severity="error">
              {excluir.error instanceof ApiError
                ? excluir.error.message
                : 'Não foi possível excluir os arquivos.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={aoFechar} disabled={excluir.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteForeverOutlinedIcon />}
          onClick={() => void confirmar()}
          disabled={motivoCurto}
          loading={excluir.isPending}
        >
          Excluir {quantidade} nota(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
